import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import AdminAvatarMenu from '@/components/Avatars/AdminAvatarMenu';
import LevelBadge from '@/components/Levels/LevelBadge';
import SkinsRow from './SkinsRow';
import { IJackpotPlayer } from '@/utils/types/IJackpotPlayer';
import { IGameItem } from '@/utils/types/Items';
import { getColor, getLevelColor } from '@/utils/colors';

interface Props {
    users: IJackpotPlayer[];
    itemsByUser: Record<number, IGameItem[]>;
    bank: number;
    gameId: number;
    highlightUserId?: number;
}

const CoinIcon = ({ size = 18 }: { size?: number }) => (
    <span
        className="rounded-full grid place-items-center shrink-0"
        style={{
            width: size,
            height: size,
            background: 'radial-gradient(circle at 35% 30%, #ffe08a, #ffc24d)',
            boxShadow: '0 0 12px rgba(255,194,77,0.7)',
        }}
    >
        <span
            className="font-black leading-none"
            style={{
                fontSize: size * 0.6,
                color: '#6a4500',
                textShadow: '0 1px 0 rgba(255,255,255,0.45)',
            }}
        >
            $
        </span>
    </span>
);

const PlayerRow = ({
    user,
    skins,
    highlight,
    gameId,
}: {
    user: IJackpotPlayer;
    skins: IGameItem[];
    highlight: boolean;
    gameId: number;
}) => {
    const color = getColor(user.color) || 'rgba(255,255,255,0.10)';
    const lvl = Math.floor((user.experience ?? 0) / 100);
    const skinWord = skins.length === 1 ? 'skin' : 'skins';

    return (
        <div
            style={
                {
                    ['--accent' as string]: color,
                    ...(highlight
                        ? {
                              background: `linear-gradient(100deg, color-mix(in srgb, ${color} 26%, transparent), color-mix(in srgb, ${color} 6%, transparent))`,
                              borderColor: color,
                              boxShadow: `0 0 28px -8px ${color}, inset 0 0 24px -8px ${color}`,
                          }
                        : {}),
                } as React.CSSProperties
            }
            className={`relative shrink-0 grid grid-cols-[max-content_minmax(0,1fr)_auto] max-md:grid-cols-[max-content_1fr] items-center gap-3.5 max-md:gap-3 py-3 pl-4 pr-3.5 rounded-fl border transition-all overflow-hidden ${
                highlight
                    ? ''
                    : 'border-stroke bg-[linear-gradient(100deg,rgba(139,109,255,0.10),rgba(139,109,255,0.02))] hover:border-stroke_strong'
            }`}
        >
            {/* left accent bar */}
            <span
                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-fl"
                style={{ background: color, boxShadow: `0 0 16px ${color}` }}
            />

            {/* avatar + meta */}
            <div className="flex items-center gap-3 min-w-0">
                <div className="relative w-12 h-12 shrink-0">
                    <AdminAvatarMenu
                        userId={user.id}
                        userName={user.name}
                        gameType="jackpot"
                        gameId={gameId}
                    >
                        <AbstractAvatar
                            seed={user.name || String(user.id)}
                            src={user.avatar}
                            size={48}
                            square
                        />
                    </AdminAvatarMenu>
                    <div className="absolute -right-1.5 -bottom-1.5">
                        <LevelBadge level={lvl} color={getLevelColor(lvl)} size="sm" variant="solid" />
                    </div>
                </div>
                <div className="min-w-0">
                    <div className="text-[15px] font-bold text-white whitespace-nowrap truncate">
                        {user.name}
                    </div>
                    <div className="text-xs text-text_3 mt-1">
                        <b style={{ color }}>{skins.length}</b> {skinWord} ·{' '}
                        {user.chancePercent.toFixed(0)}% chance
                    </div>
                </div>
            </div>

            {/* skins — fit as many as the column allows, +N for the rest */}
            <SkinsRow skins={skins} tile={50} radius={10} mobileHidden={false} />

            {/* value + chance bar */}
            <div className="text-right shrink-0 max-md:col-span-2">
                <div className="flex items-center justify-end gap-1.5 font-extrabold text-[15px] text-accent_5 whitespace-nowrap">
                    <CoinIcon size={14} />
                    {user.valueUsd?.toFixed(2)}
                </div>
                <div className="mt-1.5 ml-auto w-[120px] max-md:w-full h-1.5 rounded bg-white/[0.07] overflow-hidden">
                    <div
                        className="h-full rounded"
                        style={{
                            width: `${Math.min(100, user.chancePercent)}%`,
                            background: color,
                            boxShadow: `0 0 12px ${color}`,
                        }}
                    />
                </div>
                <div className="text-xs text-text_3 mt-1.5">
                    {user.chancePercent.toFixed(2)}%
                </div>
            </div>
        </div>
    );
};

const CurrentRound = ({ users, itemsByUser, bank, gameId, highlightUserId }: Props) => {
    const totalSkins = users.reduce(
        (acc, u) => acc + (itemsByUser[u.id]?.length ?? 0),
        0
    );

    return (
        <div className="flex flex-col gap-3.5 min-w-0 h-full min-h-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2.5 text-[15px] font-bold tracking-[0.02em]">
                    <span
                        className="w-1 h-[18px] rounded-sm shrink-0"
                        style={{
                            background: 'linear-gradient(#8b6dff,#3ad6ff)',
                            boxShadow: '0 0 12px #8b6dff',
                        }}
                    />
                    Current Round
                    <span className="text-text_3 font-medium text-[13px]">
                        · {users.length} {users.length === 1 ? 'player' : 'players'} ·{' '}
                        {totalSkins} {totalSkins === 1 ? 'skin' : 'skins'}
                    </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-fl bg-layer_2 border border-stroke font-bold text-sm text-accent_5 tabular-nums">
                    <CoinIcon size={16} />
                    {bank.toFixed(2)}
                </div>
            </div>

            {/* The list is overlaid (absolute) on the two-column desktop layout so a
                full roster scrolls inside the wheel-matched height instead of
                stretching the whole panel taller. On the stacked layout it flows
                normally with a viewport cap. */}
            <div className="relative flex-1 min-h-0 max-[1536px]:flex-none">
                <div className="fluent-scrollbar flex flex-col gap-3 overflow-y-auto overflow-x-hidden pr-2 [scrollbar-gutter:stable] max-h-[60vh] min-[1537px]:absolute min-[1537px]:inset-0 min-[1537px]:max-h-none">
                    {users.length === 0 ? (
                        <div className="flex items-center justify-center h-40 text-text_3 text-sm">
                            Waiting for players
                        </div>
                    ) : (
                        users.map((u) => (
                            <PlayerRow
                                key={u.id}
                                user={u}
                                skins={itemsByUser[u.id] ?? []}
                                highlight={highlightUserId === u.id}
                                gameId={gameId}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default CurrentRound;
